<template>
	<section class="employees-page">
		<div class="page-header">
			<div class="header-left">
				<button class="back-btn" @click="$router.go(-1)">
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</button>
				<div class="title-group">
					<h1 class="page-title">All Employees</h1>
					<p class="page-subtitle">Manage employee equity invitations and contracts</p>
				</div>
			</div>
			<div class="header-actions">
				<Search
					:type="'name'"
					:showSearchField="showSearchField"
					@search-click="showSearchField = !showSearchField"
					@search-input="filterInvites"
				/>
				<Filter
					:filterOptions="filterOptions"
					@filter-click="showFilterOptions = !showFilterOptions"
					:showFilterOptions="showFilterOptions"
					@filter-selected="filterInvites"
					@mouseleave="startTimeout"
					@mouseenter="deleteTimeout"
				/>
				<router-link :to="{ name: 'InviteEmployee' }" class="invite-link">
					<CustomButton title="Invite Employee" />
				</router-link>
			</div>
		</div>
		<div class="content-container">
			<template v-if="employeeInvite">
				<div class="tabs-wrapper">
					<TableHeader remove-padding :options="tabs" label="options" @emitOption="handleTabChange"/>
				</div>
				<div class="table-container">
					<EmployeesTable
						:tableRows="tableRows"
						:tableHeaders="tableHeaders"
						:columnNumber="1"
						:totalPage="employeeInvitePages"
						:currentPage="employeeInvitePage"
						:columnWidth="150"
						:hasActions="true"
						@go-to-page="gotoPage"
						@reload-data="fetchEmployeeInvites"
					/>
				</div>
			</template>
		</div>
	</section>
</template>

<script>
import CustomButton from "@/components/general/BtnComponent";
import EmptyState from "@/components/notification/EmptyState";
import EmployeesTable from "@/components/tables/esop/EmployeesTable";
import TableHeader from "@/components/tables/TableHeader.vue";
import {
  convertToLocale,
  formatAmountToDollar,
  formatISODate,
  truncate,
} from "@/utils/helpers.js";
import { mapActions, mapGetters } from "vuex";
// import EmployeesEmptyState from "@/components/employeesBenefit/EmployeesEmptyState";
import Filter from "@/components/general/Filter";
import Search from "@/components/general/Search";
import filter from "@/mixins/filter";
import search from "@/mixins/search";
export default {
	name: "Employees",
	data() {
		return {
			showSearchField: false,
			showFilterOptions: false,
			filterOptions: ["Accepted", "Pending", "Rejected", "Terminated"],
			invitesArray: [],
			tableHeaders: [
				"Employee name",
				"Email address",
				"Start date",
				"Allocated shares",
				"Shares exercised",
				"Contract status",
				// "Action",
				"",
			],
			tableRows: [],
			inviteTypes: [
				{
					name: "RSU",
					value: "RSU_EMPLOYEE",
				},
				{
					name: "ESOP",
					value: "ESOP_EMPLOYEE",
				},
				{
					name: "ALL",
					value: "",
				},
			],
			inviteType: "",
			tabs: ["Pending", "Current", "Exited"],
			tab: "",
			currentTab: "pending",
		};
	},
	components: {
		EmployeesTable,
		Search,
		Filter,
		// EmployeesEmptyState,
		TableHeader,
		EmptyState,
		CustomButton,
	},
	mixins: [filter, search],
	computed: {
		...mapGetters("Esop", [
			"employeeInvite",
			"employeeInvitePage",
			"employeeInvitePages",
		]),
		currentWidth() {
			return window.screen.width;
		},
	},
	watch: {
		employeeInvite(val) {
			this.invitesArray = val;
		},
		inviteType: "fetchEmployeeInvites",
		invitesArray: "updateTableRow",
    'currentTab': 'fetchEmployeeInvites'
	},
	created() {
		this.fetchEmployeeInvites();
		this.updateTableRow();
	},
	methods: {
		...mapActions("Esop", ["getEmployeeInvites"]),
		formatISODate,
		convertToLocale,
		formatAmountToDollar,
    handleTabChange(val) {
      switch (val) {
        case 'Pending':
          this.tab = val
          this.currentTab = 'pending'
          break;
        case 'Current':
          this.tab = val
          this.currentTab = 'accepted'
          break;
        case 'Exited':
          this.tab = val
          this.currentTab = 'terminated'
          break;
      }
    },
		async fetchEmployeeInvites() {
			try {
				let query = {
					page: 1,
					limit: 10,
					status: this.currentTab,
				};
				if (this.inviteType) {
					query = {
						...query,
						employee_type: this.inviteType,
					};
				}
				await this.getEmployeeInvites(query);
			} catch (error) {
				return error;
			}
		},
		filterInvites({ value, type }) {
			const { status } = value;
			if (value && value !== "All") {
				const employeeInvite = this.employeeInvite;
				const filterInvites = employeeInvite.filter((employee) => {
					if (type === "name") {
						return employee.invitation[type].toLowerCase().includes(value);
					} else {
						if (type === "status") return employee[type] === status;
					}
				});
				this.invitesArray = filterInvites;
			} else {
				this.invitesArray = this.employeeInvite;
			}
		},
		async gotoPage(page) {
			let query = { page: page, limit: 10 };
			try {
				await this.getEmployeeInvites(query);
			} catch (error) {
				return error;
			}
		},
		updateTableRow() {
			this.tableRows = this.invitesArray?.map((employee) => {
				const name = truncate(`${employee.invitation.name}`, 20);
				const email = truncate(employee.invitation.email, 18);
				const date = `${formatISODate(employee.start_date)}`;
				const allocatedShares = convertToLocale(employee.allocated_shares);
				const sharesHeld =
					employee.status === "Accepted" && employee.employee
						? convertToLocale(employee.employee?.exercised_amount)
						: "---";
				let status = null;
				switch (employee.status) {
					case "Accepted":
						status = "Signed";
						break;
					default:
						status = employee.status;
						break;
				}
				// vesting calculation:

				// X = ratio of vested esop
				let vestedEsop = null;
				// Total vesting duration in days = D
				const vestingDuration = employee.vesting_duration * 365;
				// Vesting frequency in days = C
				const vestingFrequency = employee.vesting_frequency;
				// Number of vesting cycles elapsed = A
				// A = (currentData - startDate) in days / C
				// const daysElapsed = 1825;
				const daysElapsed = Math.ceil(
					(new Date() - new Date(employee.start_date)) / (1000 * 3600 * 24)
				);
				const vestingElapsed = daysElapsed / vestingFrequency;
				// Total number of vesting cycles in duration = B
				// B = D / C
				const vestingCycles = vestingDuration / vestingFrequency;

				// X = A/B
				vestedEsop = vestingElapsed / vestingCycles;

				return {
					name,
					email,
					date,
					allocatedShares,
					sharesHeld,
					status,
					misc: `misc/${employee._id}`,
				};
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.employees-page {
	@include fadeIn;
	min-height: calc(100vh - 120px);
	overflow-x: hidden;
	padding: 24px 20px 32px;
}

.page-header {
	display: flex;
	flex-direction: column;
	gap: 20px;
	margin-bottom: 32px;

	@media screen and (min-width: 1024px) {
		flex-direction: row;
		align-items: flex-start;
		justify-content: space-between;
	}
}

.header-left {
	display: flex;
	align-items: flex-start;
	gap: 16px;
}

.back-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	background: #F8FAFC;
	border: 1px solid #E2E8F0;
	border-radius: 8px;
	color: #64748B;
	cursor: pointer;
	transition: all 0.2s ease;
	flex-shrink: 0;
	margin-top: 2px;

	&:hover {
		background: white;
		border-color: #4D84FF;
		color: #4D84FF;
	}
}

.title-group {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.page-title {
	font-family: 'DM Sans', sans-serif;
	font-size: 22px;
	font-weight: 500;
	color: #0F172A;
	margin: 0;
}

.page-subtitle {
	font-family: 'DM Sans', sans-serif;
	font-size: 14px;
	color: #64748B;
	margin: 0;
}

.header-actions {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
}

.invite-link {
	flex-shrink: 0;
}

.content-container {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.tabs-wrapper {
	background: white;
	border-radius: 10px;
	border: 1px solid #E2E8F0;
	padding: 10px 14px;
}

.table-container {
	background: white;
	border-radius: 12px;
	border: 1px solid #E2E8F0;
	box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
	overflow: hidden;
}
</style>
